import React, { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet's default icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Color palette matching Arrivo brand
const COLORS = {
  activity: '#0d9488',   // teal-600
  meal: '#f97316',       // orange-500
  hotel: '#8b5cf6',      // purple-500
  airport: '#3b82f6',    // blue-500
  default: '#64748b',    // slate-500
};

// Create numbered circle marker
const createNumberedIcon = (number, color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      width: 28px; height: 28px; border-radius: 50%;
      background: ${color}; color: white;
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 800; font-family: 'DM Sans', sans-serif;
      box-shadow: 0 2px 8px rgba(0,0,0,0.25);
      border: 2px solid white;
    ">${number}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
};

// Auto-fit bounds component
const FitBounds = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) return;
    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], 15);
      return;
    }
    const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 17 });
  }, [markers, map]);
  return null;
};

// Highlight a specific marker when selected
const HighlightMarker = ({ selectedId, markers }) => {
  const map = useMap();
  useEffect(() => {
    if (!selectedId) return;
    const marker = markers.find(m => m.id === selectedId);
    if (marker) {
      map.panTo([marker.lat, marker.lng], { animate: true, duration: 0.5 });
    }
  }, [selectedId, markers, map]);
  return null;
};

/**
 * Interactive map component for itinerary day view.
 *
 * Props:
 * - dayData: current day object from itinerary (schedule, meals, hotel)
 * - transitTimes: transit data for route styling
 * - selectedCardId: ID of currently selected/highlighted card
 * - onMarkerClick: callback when a marker is clicked (receives item)
 * - currentDay: day index for building transit keys
 */
const ItineraryMap = ({ dayData, transitTimes, selectedCardId, onMarkerClick, currentDay }) => {
  const mapRef = useRef(null);

  // Build markers from schedule + meals + hotel
  const markers = useMemo(() => {
    if (!dayData) return [];
    const result = [];
    let counter = 1;

    // Hotel marker — check both coordinates and placeData.location (enrichment stores in placeData)
    const hotelLat = dayData.hotel?.coordinates?.lat || dayData.hotel?.placeData?.location?.lat;
    const hotelLng = dayData.hotel?.coordinates?.lng || dayData.hotel?.placeData?.location?.lng;
    if (hotelLat && hotelLng) {
      result.push({
        id: 'hotel',
        lat: hotelLat,
        lng: hotelLng,
        name: dayData.hotel.name || 'Hotel',
        time: 'Hotel',
        type: 'hotel',
        number: 'H',
        color: COLORS.hotel,
      });
    }

    // Build combined and sorted list of activities + meals
    const items = [];
    (dayData.schedule || []).forEach((act, i) => {
      const lat = act.placeData?.location?.lat || act.coordinates?.lat;
      const lng = act.placeData?.location?.lng || act.coordinates?.lng;
      if (lat && lng) {
        items.push({
          id: `activity-${i}`,
          lat, lng,
          name: act.activity || act.name || 'Activity',
          time: act.time,
          duration: act.duration,
          type: 'activity',
          sortTime: parseTimeToMinutes(act.time),
          description: act.description,
          photo: act.placeData?.photo,
        });
      }
    });
    // Normalize meals: convert object format { breakfast: {}, lunch: {} } → array
    const mealsArray = Array.isArray(dayData.meals)
      ? dayData.meals
      : (dayData.meals && typeof dayData.meals === 'object')
        ? Object.values(dayData.meals).filter(v => v && typeof v === 'object')
        : [];
    mealsArray.forEach((meal, i) => {
      const lat = meal.placeData?.location?.lat || meal.coordinates?.lat;
      const lng = meal.placeData?.location?.lng || meal.coordinates?.lng;
      if (lat && lng) {
        items.push({
          id: `meal-${i}`,
          lat, lng,
          name: meal.restaurant || meal.meal || 'Meal',
          time: meal.time,
          duration: meal.duration,
          type: 'meal',
          sortTime: parseTimeToMinutes(meal.time),
          cuisine: meal.cuisine,
          photo: meal.placeData?.photo,
        });
      }
    });

    // Sort by time
    items.sort((a, b) => (a.sortTime || 0) - (b.sortTime || 0));

    // Add numbered markers
    items.forEach(item => {
      result.push({
        ...item,
        number: counter++,
        color: COLORS[item.type] || COLORS.default,
      });
    });

    return result;
  }, [dayData]);

  // Build route polyline coordinates
  const routeCoords = useMemo(() => {
    const activityMarkers = markers.filter(m => m.id !== 'hotel');
    if (activityMarkers.length < 2) return [];
    return activityMarkers.map(m => [m.lat, m.lng]);
  }, [markers]);

  // Get transport mode between stops for polyline styling
  const getSegmentStyle = (fromIdx) => {
    const key = `day${currentDay}-stop${fromIdx}-stop${fromIdx + 1}`;
    const transit = transitTimes?.[key];
    const mode = transit?.mode || 'walking';

    switch (mode) {
      case 'walking':
        return { color: COLORS.activity, weight: 2, dashArray: '6,8', opacity: 0.6 };
      case 'transit':
        return { color: '#22c55e', weight: 3, dashArray: '4,6', opacity: 0.7 };
      case 'driving':
        return { color: '#64748b', weight: 3, opacity: 0.5 };
      default:
        return { color: COLORS.activity, weight: 2, dashArray: '6,8', opacity: 0.5 };
    }
  };

  // Geocode fallback: if no markers but we have a baseCity, show a map centered on it
  const [fallbackCenter, setFallbackCenter] = React.useState(null);
  useEffect(() => {
    if (markers.length > 0 || fallbackCenter) return;
    const city = dayData?.baseCity || dayData?.city;
    if (!city) return;
    fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city.split(',')[0].trim())}&count=1&language=en`)
      .then(r => r.json())
      .then(data => {
        if (data.results?.length) {
          setFallbackCenter({ lat: data.results[0].latitude, lng: data.results[0].longitude });
        }
      })
      .catch(() => {});
  }, [markers.length, dayData?.baseCity, dayData?.city, fallbackCenter]);

  if (markers.length === 0 && !fallbackCenter) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 text-gray-400 rounded-xl">
        <div className="text-center">
          <p className="text-sm font-semibold">No location data available</p>
          <p className="text-xs mt-1">Activities need coordinates from Google Places enrichment</p>
        </div>
      </div>
    );
  }

  // If we only have a fallback center (no markers), show a basic map centered on the city
  if (markers.length === 0 && fallbackCenter) {
    return (
      <div className="relative w-full h-full rounded-xl overflow-hidden">
        <MapContainer
          center={[fallbackCenter.lat, fallbackCenter.lng]}
          zoom={15}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={[fallbackCenter.lat, fallbackCenter.lng]}
            icon={createNumberedIcon('📍', COLORS.default)}
          >
            <Popup>{dayData?.baseCity || 'Destination'}</Popup>
          </Marker>
        </MapContainer>
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs text-gray-500 shadow-sm">
          Detailed markers will appear after Places enrichment
        </div>
      </div>
    );
  }

  // Default center: first marker
  const center = [markers[0].lat, markers[0].lng];

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden">
      <MapContainer
        ref={mapRef}
        center={center}
        zoom={15}
        style={{ width: '100%', height: '100%' }}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds markers={markers} />
        <HighlightMarker selectedId={selectedCardId} markers={markers} />

        {/* Route polylines */}
        {routeCoords.length >= 2 && routeCoords.slice(0, -1).map((coord, i) => (
          <Polyline
            key={`route-${i}`}
            positions={[coord, routeCoords[i + 1]]}
            pathOptions={getSegmentStyle(i)}
          />
        ))}

        {/* Markers */}
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={[marker.lat, marker.lng]}
            icon={createNumberedIcon(marker.number || 'H', marker.color)}
            eventHandlers={{
              click: () => {
                if (onMarkerClick) onMarkerClick(marker);
              },
            }}
          >
            <Popup>
              <div style={{ fontFamily: "'DM Sans', sans-serif", minWidth: '160px' }}>
                <div style={{ fontWeight: 800, fontSize: '14px', marginBottom: '4px' }}>
                  {marker.name}
                </div>
                {marker.time && marker.time !== 'Hotel' && (
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>
                    {marker.time}{marker.duration ? ` \u00B7 ${marker.duration}` : ''}
                  </div>
                )}
                {marker.cuisine && (
                  <div style={{ fontSize: '11px', color: '#f97316', fontWeight: 600 }}>
                    {marker.cuisine}
                  </div>
                )}
                {marker.description && (
                  <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px', lineHeight: '1.4' }}>
                    {marker.description.substring(0, 100)}{marker.description.length > 100 ? '...' : ''}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Legend */}
      <div
        className="absolute bottom-3 left-3 z-[1000] bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md"
        style={{ fontSize: '10px', fontFamily: "'DM Sans', sans-serif" }}
      >
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.activity, display: 'inline-block' }} />
            Activity
          </span>
          <span className="flex items-center gap-1">
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.meal, display: 'inline-block' }} />
            Meal
          </span>
          <span className="flex items-center gap-1">
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.hotel, display: 'inline-block' }} />
            Hotel
          </span>
        </div>
      </div>
    </div>
  );
};

// Simple time parser (HH:MM or H:MM AM/PM to minutes)
function parseTimeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const str = String(timeStr).trim();

  // 24h format
  const match24 = str.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) return parseInt(match24[1]) * 60 + parseInt(match24[2]);

  // 12h format
  const match12 = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    let h = parseInt(match12[1]);
    const m = parseInt(match12[2]);
    const period = match12[3].toUpperCase();
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }

  return 0;
}

export default React.memo(ItineraryMap);
